import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BookOpen, FileText } from 'lucide-react';
import { KBSidebar } from '@/components/knowledge-base/KBSidebar';
import { KBArticleView } from '@/components/knowledge-base/KBArticleView';
import { KBEditor } from '@/components/knowledge-base/KBEditor';
import { KBSearch } from '@/components/knowledge-base/KBSearch';
import { useArticle, useArticles } from '@/hooks/useKnowledgeBase';
import { useUserRole } from '@/hooks/useUserRole';
import { Skeleton } from '@/components/ui/skeleton';

interface KnowledgeBaseProps {
  mode?: 'view' | 'create' | 'edit';
}

export default function KnowledgeBase({ mode = 'view' }: KnowledgeBaseProps) {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { role } = useUserRole();
  const isAdmin = role === 'admin';

  const { data: article, isLoading: articleLoading } = useArticle(slug);
  const { data: articles = [] } = useArticles();

  // Redirect to first article if no slug provided
  useEffect(() => {
    if (mode === 'view' && !slug && articles.length > 0) {
      navigate(`/knowledge-base/${articles[0].slug}`, { replace: true });
    }
  }, [mode, slug, articles, navigate]);

  // Check admin access for create/edit modes
  if ((mode === 'create' || mode === 'edit') && !isAdmin) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-foreground">Acesso Negado</h2>
          <p className="text-muted-foreground mt-2">Apenas administradores podem editar artigos.</p>
        </div>
      </div>
    );
  }

  // Create mode
  if (mode === 'create') {
    return <KBEditor mode="create" />;
  }

  // Edit mode
  if (mode === 'edit') {
    if (articleLoading) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      );
    }
    if (!article) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-foreground">Artigo não encontrado</h2>
          </div>
        </div>
      );
    }
    return <KBEditor article={article} mode="edit" />;
  }

  // View mode
  return (
    <div className="flex-1 flex flex-col min-h-0 bg-background">
      {/* Header */}
      <div className="border-b border-border px-6 py-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <BookOpen className="h-6 w-6 text-primary" />
          <h1 className="text-xl font-semibold">Knowledge Base</h1>
        </div>
        <div className="w-72">
          <KBSearch />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex min-h-0">
        <KBSidebar currentSlug={slug} />

        {articleLoading ? (
          <div className="flex-1 p-6">
            <Skeleton className="h-8 w-64 mb-4" />
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ) : article ? (
          <KBArticleView article={article} />
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Selecione um artigo no menu lateral</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
